import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import '../../styles/components/auth/terms_layout.css';

const TermsOfUse = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [scrollProgress, setScrollProgress] = useState(0);
  const [hasReadCompletely, setHasReadCompletely] = useState(false);
  
  // Get registration data from navigation state
  const registrationData = location.state?.registrationData;
  const tenantId = location.state?.tenantId;

  // Handle scroll tracking
  const handleScroll = () => {
    const scrollTop = window.scrollY;
    const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
    const progress = (scrollTop / scrollHeight) * 100;
    
    setScrollProgress(progress);
    
    // Mark as read when user scrolls to at least 90%
    if (progress >= 90) {
      setHasReadCompletely(true);
    }
  };

  // Use window scroll event instead of element scroll
  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNext = () => {
    if (hasReadCompletely) {
      // Navigate to Privacy Policy with registration data
      navigate('/privacy-policy', {
        state: {
          registrationData: registrationData,
          tenantId: tenantId,
          termsAccepted: true
        }
      });
    }
  };

  const handleSkip = () => {
    // Go back to registration form
    navigate('/register');
  };

  return (
    <div className="terms-layout">
      <div className="terms-header">
        <h1>Neurolex Terms of Use</h1>
        <p>These terms outline the rules and conditions for using the Neurolex platform and its services responsibly and securely.</p>
        
        <div className="reading-progress">
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${scrollProgress}%` }}
            ></div>
          </div>
          <span className="progress-text">
            {Math.round(scrollProgress)}% read
          </span>
        </div>
      </div>

      <div className="terms-content-wrapper">
        <div className="terms-content">
          <div className="content-inner">
            <h2>1. Introduction</h2>
            <p>
              Welcome to the Neurolex Mental Health Professional Portal ("Portal"), a web-based platform designed to assist licensed mental health professionals in managing patient care, scheduling, communication, and analysis through AI-assisted tools. These Terms of Use ("Terms") form a binding agreement between you ("Professional," "you," or "your") and Neurolex ("we," "us," or "our"). By accessing or using the Portal, you agree to comply with and be bound by these Terms. If you do not agree with these Terms, please do not use the Portal.
            </p>

            <h2>2. Eligibility and Account Registration</h2>
            
            <h3 className="subsection-title">2.1 Professional Eligibility</h3>
            <p>
              Access to the Portal is strictly limited to licensed and verified mental health professionals. You must provide accurate and complete credentials, including licenses and certifications, which will be reviewed and approved before access is granted.
            </p>

            <h3 className="subsection-title">2.2 Account Creation and Security</h3>
            <p>
              You agree to provide truthful, up-to-date information when registering and maintain the confidentiality of your account credentials. You are responsible for all activities conducted under your account.
            </p>

            <h3 className="subsection-title">2.3 Account Access</h3>
            <p>
              You must notify us immediately of any unauthorized use or security breach related to your account.
            </p>

            <h2>3. Use of the Portal</h2>
            
            <h3 className="subsection-title">3.1 Authorized Use</h3>
            <p>
              You agree to use the Portal solely for professional purposes related to mental health care provision, patient communication, scheduling, and analysis.
            </p>

            <h3 className="subsection-title">3.2 Compliance with Laws</h3>
            <p>
              You shall comply with all applicable laws, regulations, and professional standards in your jurisdiction when using the Portal.
            </p>

            <h3 className="subsection-title">3.3 Prohibited Conduct</h3>
            <p>You must not:</p>
            <ul className="terms-list">
              <li className="terms-list-item">Use the Portal to engage in unlawful, unethical, or harmful conduct.</li>
              <li className="terms-list-item">Attempt unauthorized access to data or systems.</li>
              <li className="terms-list-item">Share or disclose patient information outside the bounds of professional confidentiality and applicable privacy laws.</li>
              <li className="terms-list-item">Use the Portal to transmit malware or harmful code.</li>
            </ul>

            <h2>4. Professional Relationship and Liability</h2>
            
            <h3 className="subsection-title">4.1 Independent Professional Services</h3>
            <p>
              The Portal is a tool to facilitate your professional activities but does not provide mental health services itself. Any clinical advice, diagnosis, or treatment is your sole responsibility.
            </p>

            <h3 className="subsection-title">4.2 No Medical Advice by Neurolex</h3>
            <p>
              We do not offer medical advice or guarantee the accuracy of AI-generated insights. You must use your professional judgment in all patient-related decisions.
            </p>

            <h3 className="subsection-title">4.3 Limitation of Liability</h3>
            <p>
              To the maximum extent permitted by law, Neurolex is not liable for any direct or indirect damages arising from your use of the Portal. This includes, but is not limited to, errors in data, service interruptions, or decisions made based solely on AI-generated information provided through the Portal.
            </p>

            <h2>5. Intellectual Property</h2>
            <p>
              All content, software, and technology provided in the Portal are owned or licensed by Neurolex. You may not copy, distribute, modify, or create derivative works without prior written consent.
            </p>

            <h2>6. Privacy and Data Security</h2>
            <p>
              Your use of the Portal is subject to our Privacy Policy, which governs how we collect, use, and protect your personal and professional data.
            </p>
            <p>
              You agree to maintain patient confidentiality and comply with all applicable privacy laws related to patient data accessed or processed via the Portal.
            </p>

            <h2>7. Payments and Fees</h2>
            <p>
              If applicable, any payments related to patient services facilitated through the Portal are handled directly between you and your patients. Neurolex does not process payments or hold financial responsibility for such transactions.
            </p>

            <h2>8. Termination</h2>
            
            <h3 className="subsection-title">8.1 Termination by You</h3>
            <p>
              You may deactivate your account at any time via the Portal.
            </p>

            <h3 className="subsection-title">8.2 Termination by Us</h3>
            <p>
              We reserve the right to suspend or terminate your access for violations of these Terms, inactivity, or legal reasons.
            </p>

            <h3 className="subsection-title">8.3 Effect of Termination</h3>
            <p>
              Termination does not affect rights or obligations accrued prior to termination, including payment obligations and confidentiality.
            </p>

            <h2>9. Changes to Terms</h2>
            <p>
              We may update these Terms periodically. Changes will be posted on the Portal with an updated effective date. Continued use after changes constitutes acceptance of the updated Terms.
            </p>

            <h2>10. Governing Law and Dispute Resolution</h2>
            <p>
              These Terms are governed by the laws of the Philippines. Any disputes arising from these Terms or use of the Portal will be resolved first through informal negotiation, then by binding arbitration in the Philippines.
            </p>

            <h2>11. General Provisions</h2>
            
            <h3 className="subsection-title">11.1 Entire Agreement</h3>
            <p>
              These Terms of Use, together with the Privacy Policy and any other applicable agreements or guidelines presented within the Neurolex Mental Health Professional Portal, constitute the entire agreement between you and Neurolex concerning your use of the Portal. They supersede any prior agreements, representations, or understandings, whether written or oral.
            </p>

            <h3 className="subsection-title">11.2 Waiver and Severability</h3>
            <p>
              Our failure to enforce any right or provision of these Terms will not constitute a waiver of such right or provision. If any part of these Terms is found to be invalid or unenforceable under applicable law, the remaining portions will remain in full force and effect.
            </p>

            <h3 className="subsection-title">11.3 Assignment</h3>
            <p>
              You may not assign or transfer your rights or obligations under these Terms without prior written consent from Neurolex. We may assign or transfer our obligations at any time as part of a business restructuring, merger, or transfer of assets.
            </p>

            <h3 className="subsection-title">11.4 No Partnership or Agency</h3>
            <p>
              Nothing in these Terms creates a partnership, joint venture, employment, or agency relationship between you and Neurolex. You acknowledge that you are providing services to patients independently and not as an agent or representative of Neurolex.
            </p>

            <h2>12. Contact Information</h2>
            <p>If you have any questions, concerns, or feedback regarding these Terms or your use of the Mental Health Professional Portal, please contact us at:</p>
            <ul className="contact-list">
              <li className="contact-list-item"><strong>Email:</strong> neurolex.life@gmail.com</li>
              <li className="contact-list-item"><strong>Phone:</strong> +63 966 893 6783</li>
            </ul>

            <div className="terms-footer">
              <p>
                <strong>By registering for or using the Neurolex Mental Health Professional Portal, you confirm that you have read, understood, and agreed to be legally bound by these Terms of Use.</strong>
              </p>
              <p><strong>Last updated:</strong> {new Date().toLocaleDateString()}</p>
              <p><strong>Effective date:</strong> {new Date().toLocaleDateString()}</p>
            </div>

            {/* Navigation Buttons */}
            <div className="inline-navigation-buttons">
              <button onClick={handleSkip} className="btn-inline-back">
                ← Back to Registration
              </button>
              <button 
                onClick={handleNext} 
                className={`btn-inline-next ${!hasReadCompletely ? 'disabled' : ''}`}
                disabled={!hasReadCompletely}
              >
                Next: Privacy Policy →
              </button>
            </div>

            {!hasReadCompletely && (
              <p className="inline-reading-instruction">
                Please scroll to read the complete Terms of Use to continue
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfUse;